import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { adminGet } from "../services/api";
import {
  FlashRegular, ClockRegular, WarningRegular, CheckmarkCircleRegular, NumberSymbolRegular, ArrowSyncRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const Zap = fluentIcon(FlashRegular);
const Clock = fluentIcon(ClockRegular);
const AlertTriangle = fluentIcon(WarningRegular);
const CheckCircle = fluentIcon(CheckmarkCircleRegular);
const Hash = fluentIcon(NumberSymbolRegular);
const Loader2 = fluentIcon(ArrowSyncRegular);

interface AIStats {
  summary_24h: {
    total_calls: number;
    success: number;
    errors: number;
    total_tokens: number;
    prompt_tokens: number;
    completion_tokens: number;
    avg_latency_ms: number;
  };
  by_provider: {
    provider: string;
    calls: number;
    success: number;
    errors: number;
    avg_latency_ms: number;
    total_tokens: number;
    last_used: string;
  }[];
  by_feature: {
    feature: string;
    calls: number;
    avg_latency_ms: number;
    total_tokens: number;
  }[];
  recent: {
    provider: string;
    model: string;
    feature: string;
    status: string;
    latency_ms: number;
    total_tokens: number;
    error_msg: string | null;
    created_at: string;
  }[];
  hourly: { hour: string; calls: number; errors: number }[];
}

export default function AIAnalyticsPage() {
  const { token } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["ai-analytics"],
    queryFn: () => adminGet<AIStats>("/api/v1/admin/stats/ai", token),
    refetchInterval: 30_000,
  });

  const s = data?.summary_24h;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">AI Analytics</h1>
        <p className="text-sm text-slate-500 mt-1">Real-time monitoring of all AI/LLM calls. Provider performance, token usage, errors.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card label="Total Calls (24h)" value={s?.total_calls ?? "—"} icon={<Zap size={16} />} />
        <Card label="Success Rate" value={s ? `${s.total_calls ? Math.round(s.success / s.total_calls * 100) : 0}%` : "—"} icon={<CheckCircle size={16} />} color={s && s.errors > 0 ? "text-yellow-400" : "text-emerald-400"} />
        <Card label="Avg Latency" value={s ? `${(s.avg_latency_ms / 1000).toFixed(1)}s` : "—"} icon={<Clock size={16} />} />
        <Card label="Tokens Used" value={s?.total_tokens?.toLocaleString() ?? "—"} icon={<Hash size={16} />} />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* By Provider */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-400 mb-4">By Provider (24h)</h3>
          {isLoading ? <Loader2 size={16} className="animate-spin text-slate-500" /> : (
            <div className="space-y-3">
              {(data?.by_provider ?? []).map(p => (
                <div key={p.provider} className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-slate-200">{p.provider}</span>
                    <span className="text-xs text-slate-500 ml-2">{p.calls} calls</span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-emerald-400">{p.success} ok</span>
                    {p.errors > 0 && <span className="text-red-400">{p.errors} err</span>}
                    <span className="text-slate-500">{(p.avg_latency_ms / 1000).toFixed(1)}s</span>
                    <span className="text-slate-500">{p.total_tokens.toLocaleString()} tok</span>
                  </div>
                </div>
              ))}
              {(data?.by_provider ?? []).length === 0 && <p className="text-sm text-slate-500">No AI calls in the last 24h.</p>}
            </div>
          )}
        </div>

        {/* By Feature */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-400 mb-4">By Feature (24h)</h3>
          {isLoading ? <Loader2 size={16} className="animate-spin text-slate-500" /> : (
            <div className="space-y-3">
              {(data?.by_feature ?? []).map(f => (
                <div key={f.feature} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-200 capitalize">{f.feature}</span>
                  <div className="flex gap-3 text-xs">
                    <span className="text-brand-blue">{f.calls} calls</span>
                    <span className="text-slate-500">{(f.avg_latency_ms / 1000).toFixed(1)}s avg</span>
                    <span className="text-slate-500">{f.total_tokens.toLocaleString()} tok</span>
                  </div>
                </div>
              ))}
              {(data?.by_feature ?? []).length === 0 && <p className="text-sm text-slate-500">No feature data yet.</p>}
            </div>
          )}
        </div>
      </div>

      {/* Recent calls log */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-400 mb-4">Recent AI Calls</h3>
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-800">
                  <th className="text-left py-2 pr-3">Time</th>
                  <th className="text-left py-2 pr-3">Provider</th>
                  <th className="text-left py-2 pr-3">Model</th>
                  <th className="text-left py-2 pr-3">Feature</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-right py-2 pr-3">Latency</th>
                  <th className="text-right py-2">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent ?? []).map((c, i) => (
                  <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="py-2 pr-3 text-slate-400">{new Date(c.created_at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                    <td className="py-2 pr-3 text-slate-200 font-medium">{c.provider}</td>
                    <td className="py-2 pr-3 text-slate-400 truncate max-w-[120px]">{c.model}</td>
                    <td className="py-2 pr-3 text-slate-400 capitalize">{c.feature}</td>
                    <td className="py-2 pr-3">
                      {c.status === "success"
                        ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle size={10} /> ok</span>
                        : <span className="text-red-400 flex items-center gap-1" title={c.error_msg ?? ""}><AlertTriangle size={10} /> err</span>
                      }
                    </td>
                    <td className="py-2 pr-3 text-right text-slate-400">{c.latency_ms ? `${(c.latency_ms / 1000).toFixed(1)}s` : "—"}</td>
                    <td className="py-2 text-right text-slate-400">{c.total_tokens ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(data?.recent ?? []).length === 0 && <p className="text-sm text-slate-500 text-center py-4">No AI calls logged yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, icon, color }: { label: string; value: string | number; icon?: React.ReactNode; color?: string }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider mb-2">{icon} {label}</div>
      <div className={`text-xl font-semibold ${color ?? "text-slate-200"}`}>{value}</div>
    </div>
  );
}
